import { Spinner, Text, TokenTextAppearance, TokenTextColor, styled } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useGetInstalaciones } from 'src/feature-app/hooks';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { Magnifier } from 'src/icons-core-imports';
import { Installation, OneFormState } from 'src/types';
import { InputWrapper } from './styleComponents';

interface SuggestionWrapperProps {
	error?: boolean;
}

const SuggestionWrapper = styled.div<SuggestionWrapperProps>`
	position: relative;
	width: 100%;

	& > input {
	}

	.react-autosuggest__container {
		position: relative;
	}

	.react-autosuggest__input {
		width: 100%;
		padding: 16px 16px 16px 40px;
		border: ${(props) => (props.error ? '2px solid rgb(228, 0, 44)' : '1px solid rgb(194, 202, 207)')};
		border-radius: 8px;
	}

	.react-autosuggest__suggestions-container--open {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		z-index: 1000;
		background: white;
		border: 1px solid rgb(194, 202, 207);
		border-top: none;
		border-radius: 0 0 4px 4px;
		max-height: 200px;
		overflow-y: auto;
	}

	.react-autosuggest__suggestions-list {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.react-autosuggest__suggestion {
		padding: 12px;
		cursor: pointer;
		border-bottom: 1px solid #eee;
	}

	.react-autosuggest__suggestion--highlighted {
		background-color: #f5f5f5;
	}
`;

const MagnifierIcon = styled.div`
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
	pointer-events: none;
	color: #666;
`;

const getSuggestions = (value: string, list: Installation[]) => {
	if (value.length < 2) return [];
	const regex = new RegExp(value.trim(), 'i');
	return list.filter((installation: Installation) => regex.test(installation.VGED_CommercialName__c || '') || regex.test(installation.ShippingCity || '') || regex.test(installation.Province || ''));
};

const getSuggestionValue = (suggestion: Installation) => suggestion.VGED_CommercialName__c;

const renderSuggestion = (suggestion: Installation) => (
	<div style={{ padding: '8px 0' }}>
		<Text appearance={TokenTextAppearance.copy200} color={TokenTextColor.inherit}>
			<Text bold>{suggestion.VGED_CommercialName__c}</Text> ({suggestion.KVPSCode__c})<br />
			{suggestion.ShippingStreet} ({suggestion.ShippingCity})
		</Text>
	</div>
);

interface Props {
	dpsDomain?: string;
	dpsToken?: string;
	required?: boolean;
	paddingTop?: string;
	paddingBottom?: string;
}

export const SuggestInstallation = (props: Props) => {
	const { dpsDomain, dpsToken, required, paddingTop, paddingBottom } = props;
	const name = 'selectedInstallation';

	const dispatch = useDispatch();
	const { register, errors } = useFormContext();
	const [value, setValue] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const [suggestion, setSuggestion] = useState<Installation>(null);

	const { setDisableIntroNextStep, setError } = useOneFormContext();
	const { formData, dealersInfo } = useSelector((state: OneFormState) => state);
	const { selectedInstallation } = dealersInfo;
	const { fields } = formData;

	useEffect(() => {
		if (selectedInstallation && !suggestion) {
			setSuggestion(selectedInstallation);
		}
	}, [selectedInstallation]);

	const { installations, loading } = useGetInstalaciones(dpsToken ? dpsToken : 'LCV_DDB_FRM_LEM_REC_LEM', dpsDomain ? dpsDomain : 'VOLKSWAGEN_SF_LEAD_INSTALLATION', 'vn');

	// If form has kvps value, try to find matching installation and select it
	useEffect(() => {
		const rawKvps = fields && (fields as any)['kvps'];
		const kvpsValue = rawKvps !== undefined && rawKvps !== null ? String(rawKvps).trim().toUpperCase() : '';
		if (!kvpsValue || !installations || installations.length === 0) return;

		const installation = installations.find((inst: Installation) => {
			const instKvps = inst && inst.KVPSCode__c ? String(inst.KVPSCode__c).trim().toUpperCase() : '';
			return instKvps === kvpsValue;
		});
		if (installation) {
			setSuggestion(installation);
			setValue(getSuggestionValue(installation));
			dispatch({ type: 'SET_SELECTED_INSTALLATION', payload: installation });
		}
	}, [fields, installations]);

	const onChange = (e: any, { newValue }: { newValue: string }) => {
		if (newValue === '') {
			setSuggestion(null);
		}
		setError(false);
		setValue(newValue);
	};

	const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
		if (installations) {
			setSuggestions(getSuggestions(value, installations));
		}
	};

	const onSuggestionsClearRequested = () => {
		setSuggestions([]);
	};

	const inputProps = {
		placeholder: 'Buscar instalación' + (required ? '*' : ''),
		value,
		onChange,
		onBlur: () => setDisableIntroNextStep(false),
	};

	const isRequiredRef = register({
		required: {
			value: required,
			message: required ? 'Requerido' : '',
		},
	});

	return (
		<InputWrapper paddingTop={paddingTop} paddingBottom={paddingBottom}>
			<SuggestionWrapper error={errors[name]}>
				<MagnifierIcon>{loading ? <Spinner variant="default" /> : <Magnifier />}</MagnifierIcon>
				<Autosuggest
					suggestions={suggestions}
					onSuggestionsFetchRequested={onSuggestionsFetchRequested}
					onSuggestionsClearRequested={onSuggestionsClearRequested}
					getSuggestionValue={getSuggestionValue}
					renderSuggestion={renderSuggestion}
					onSuggestionSelected={(event, { suggestion }) => {
						setSuggestion(suggestion);
						dispatch({ type: 'SET_SELECTED_INSTALLATION', payload: suggestion });
					}}
					inputProps={inputProps}
				/>
			</SuggestionWrapper>
			<input type="hidden" name={name} value={suggestion ? getSuggestionValue(suggestion) : ''} ref={isRequiredRef} />
		</InputWrapper>
	);
};
